<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { NDrawer, NDrawerContent } from 'naive-ui'
import { useNavSignals } from '../composables/useNavSignals'
import { activeGroupKey } from '../lib/navConfig'
import AppNavRail from './AppNavRail.vue'

// Phone navigation: a bottom tab bar for the four places used most —
// Today, Ask, the Brain and the Factory — plus Menu, which opens the
// full grouped nav (the same AppNavRail the desktop shows) in a drawer,
// so nothing the desktop rail offers is out of reach on a phone.

const emit = defineEmits<{
  (e: 'add-to-wiki'): void
  (e: 'ask'): void
  (e: 'search'): void
}>()

const route = useRoute()
const { isAdmin, reviewCount } = useNavSignals()
const menuOpen = ref(false)

type TabKey = 'today' | 'brain' | 'factory'

interface RouteTab {
  key: TabKey
  label: string
  to: string
}

const tabs = computed<RouteTab[]>(() => {
  const list: RouteTab[] = [
    { key: 'today', label: 'Today', to: '/' },
    { key: 'brain', label: 'Brain', to: '/p/index.md' },
  ]
  if (isAdmin.value) list.push({ key: 'factory', label: 'Factory', to: '/agent' })
  return list
})

const activeTab = computed<TabKey | null>(() => {
  if (route.path === '/') return 'today'
  const group = activeGroupKey(route.path)
  return group === 'brain' || group === 'factory' ? group : null
})

const TAB_DOT: Record<TabKey, string> = {
  today: 'bg-ink',
  brain: 'bg-brand-primary',
  factory: 'bg-factory',
}

function tabLabel(tab: RouteTab): string {
  if (tab.key === 'brain' && isAdmin.value && reviewCount.value > 0) {
    return `${tab.label}, ${reviewCount.value} open for review`
  }
  return tab.label
}

const tabClass = 'flex min-h-[52px] flex-col items-center justify-center gap-1 text-xs font-medium no-underline'
</script>

<template>
  <nav
    aria-label="Main"
    data-testid="app-tab-bar"
    class="grid grid-cols-5 border-t border-border-divider bg-surface-rail px-1.5 pt-1 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
  >
    <RouterLink
      v-for="tab in tabs.slice(0, 1)"
      :key="tab.key"
      :to="tab.to"
      :data-testid="`tabbar-${tab.key}`"
      :aria-current="activeTab === tab.key ? 'page' : undefined"
      :class="[tabClass, activeTab === tab.key ? 'text-ink' : 'text-ink-muted']"
    >
      <span
        class="h-1.5 w-1.5 rounded-full"
        :class="activeTab === tab.key ? TAB_DOT[tab.key] : ''"
        aria-hidden="true"
      />
      {{ tab.label }}
    </RouterLink>
    <button type="button" data-testid="tabbar-ask" :class="[tabClass, 'text-ink-muted']" @click="emit('ask')">
      <span class="h-4" aria-hidden="true" />
      Ask
    </button>
    <RouterLink
      v-for="tab in tabs.slice(1)"
      :key="tab.key"
      :to="tab.to"
      :data-testid="`tabbar-${tab.key}`"
      :aria-label="tabLabel(tab)"
      :aria-current="activeTab === tab.key ? 'page' : undefined"
      :class="[tabClass, activeTab === tab.key ? 'text-ink' : 'text-ink-muted']"
    >
      <span class="flex h-4 items-center gap-1" aria-hidden="true">
        <span class="h-1.5 w-1.5 rounded-full" :class="activeTab === tab.key ? TAB_DOT[tab.key] : ''" />
        <span
          v-if="tab.key === 'brain' && isAdmin && reviewCount > 0"
          data-testid="tabbar-brain-badge"
          class="rounded-full bg-attention px-1.5 text-[11px] leading-4 font-bold text-[#0e1a2e]"
          >{{ reviewCount }}</span
        >
      </span>
      {{ tab.label }}
    </RouterLink>
    <button
      type="button"
      data-testid="tabbar-menu"
      aria-haspopup="dialog"
      :aria-expanded="menuOpen"
      :class="[tabClass, 'text-ink-muted', isAdmin ? '' : 'col-start-5']"
      @click="menuOpen = true"
    >
      <span class="h-4" aria-hidden="true" />
      Menu
    </button>

    <NDrawer v-model:show="menuOpen" placement="left" :width="300">
      <NDrawerContent :native-scrollbar="false" body-content-style="padding: 0">
        <AppNavRail
          variant="drawer"
          @add-to-wiki="emit('add-to-wiki')"
          @ask="emit('ask')"
          @search="emit('search')"
          @navigate="menuOpen = false"
        />
      </NDrawerContent>
    </NDrawer>
  </nav>
</template>
